import React, { useContext, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { AuthContext } from "../../../Context/AuthContextProvider";
import { useNavigate } from "react-router-dom";
import { post } from "../../../utils/api";
import {
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Shield,
  Key,
  AlertCircle,
} from "lucide-react";

function ChangePassword() {
  const { loginUser } = useContext(AuthContext);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const navigate = useNavigate();

  // Validation criteria
  const passwordCriteria = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    digit: /\d/.test(newPassword),
    specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    match: newPassword === confirmPassword && confirmPassword.length > 0,
  };

  // Check if all conditions are met
  const isPasswordValid =
    passwordCriteria.length &&
    passwordCriteria.uppercase &&
    passwordCriteria.lowercase &&
    passwordCriteria.digit &&
    passwordCriteria.specialChar &&
    passwordCriteria.match;

  useEffect(() => {
    if (newPassword || confirmPassword) {
      setShowValidation(true);
    } else {
      setShowValidation(false);
    }
  }, [newPassword, confirmPassword]);

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword === oldPassword) {
      toast.error("New password must be different from the old password");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match.");
      return;
    }

    if (!isPasswordValid) {
      toast.error("New password does not meet the requirements");
      return;
    }

    setLoading(true);
    const userId = loginUser._id;

    try {
      const response = await post("/sam/doc/users/change-password", {
        userId,
        oldPassword,
        newPassword,
      });

      toast.success(response.message || "Password changed successfully");

      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowValidation(false);

      navigate("/Dashboard/Profile");
    } catch (error) {
      // Extract error message from response
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Error changing password";

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 md:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-[#b99653] to-[#d4af37] rounded-xl shadow-lg">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-Black-900">
              Change Password
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Update your account password
            </p>
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
            <Shield className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-blue-900">Security Tip</p>
            <p className="text-xs text-blue-700 mt-1">
              Choose a strong password that you don't use anywhere else. Never
              share your password with anyone.
            </p>
          </div>
        </div>

        {/* Change Password Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-[#fcdb6a]/20 to-[#b99653]/10 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-[#b99653] to-[#d4af37] rounded-lg">
                <Key className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Password Settings
              </h2>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleChangePassword} className="p-6 space-y-6">
            {/* Old Password */}
            <div className="space-y-2">
              <label
                htmlFor="oldPassword"
                className="flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <Lock className="w-4 h-4 text-[#b99653]" />
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="oldPassword"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                  placeholder="Enter your current password"
                  className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:border-[#b99653] focus:ring-2 focus:ring-[#b99653]/20 transition-all outline-none text-gray-900 font-medium"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-500" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-500" />
                  )}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <label
                htmlFor="newPassword"
                className="flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <Lock className="w-4 h-4 text-[#b99653]" />
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setShowValidation(true);
                  }}
                  required
                  placeholder="Enter your new password"
                  className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:border-[#b99653] focus:ring-2 focus:ring-[#b99653]/20 transition-all outline-none text-gray-900 font-medium"
                />
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label
                htmlFor="confirmPassword"
                className="flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <Lock className="w-4 h-4 text-[#b99653]" />
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setShowValidation(true);
                  }}
                  required
                  placeholder="Re-enter your new password"
                  className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:border-[#b99653] focus:ring-2 focus:ring-[#b99653]/20 transition-all outline-none text-gray-900 font-medium"
                />
              </div>
            </div>

            {/* Password Requirements */}
            {showValidation && (
              <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-[#b99653]" />
                  Password Requirements
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="flex items-center gap-2">
                    {passwordCriteria.length ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        passwordCriteria.length
                          ? "text-green-700 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      At least 8 characters
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {passwordCriteria.uppercase ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        passwordCriteria.uppercase
                          ? "text-green-700 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      One uppercase letter
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {passwordCriteria.lowercase ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        passwordCriteria.lowercase
                          ? "text-green-700 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      One lowercase letter
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {passwordCriteria.digit ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        passwordCriteria.digit
                          ? "text-green-700 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      One number
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {passwordCriteria.specialChar ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        passwordCriteria.specialChar
                          ? "text-green-700 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      One special character
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {passwordCriteria.match ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm ${
                        passwordCriteria.match
                          ? "text-green-700 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      Passwords match
                    </span>
                  </div>
                </div>

                {/* Overall Status */}
                {isPasswordValid && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-sm font-semibold">
                        All requirements met! You can change your password.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Show Password Toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showPassword"
                checked={showPassword}
                onChange={() => setShowPassword(!showPassword)}
                className="w-4 h-4 rounded border-gray-300 text-[#b99653] focus:ring-[#b99653] cursor-pointer"
              />
              <label
                htmlFor="showPassword"
                className="text-sm text-gray-700 cursor-pointer select-none"
              >
                Show passwords
              </label>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={loading || !isPasswordValid || !oldPassword}
                className={`w-full  white-btn !border-[#b99653] flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                  loading || !isPasswordValid || !oldPassword
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white hover:shadow-lg hover:scale-[1.02]"
                }`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Changing Password...</span>
                  </>
                ) : (
                  <>
                    <Lock className=" " />
                    <span className="">Change Password</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Additional Info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
          <div className="p-2 bg-yellow-100 rounded-lg flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-yellow-700" />
          </div>
          <div>
            <p className="text-sm font-medium text-yellow-900">
              Forgot Your Password?
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              If you don't remember your current password, please log out and
              use the "Forgot Password" option on the login page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChangePassword;
